import type { 
  AIClient, 
  AIAnalysisRequest, 
  AIAnalysisResult, 
  FallbackConfig,
  AISettings
} from '$lib/types/ai';
import { StreamingAIClient } from './StreamingAIClient';
import { AIClientFactory } from '../AIClientFactory';

/**
 * Anthropic Streaming Client with SSE support
 * Extends StreamingAIClient to provide Anthropic Claude-specific streaming implementation
 */
export class AnthropicStreamingClient extends StreamingAIClient {
  private apiKey: string;
  private model: string;
  private maxTokens: number;
  
  constructor(
    apiKey: string, 
    model: string, 
    maxTokens: number,
    config: FallbackConfig,
    currentSettings: AISettings
  ) {
    super(config, currentSettings);
    this.apiKey = apiKey;
    this.model = model;
    this.maxTokens = maxTokens;
  }

  async analyzeCorrections(request: AIAnalysisRequest): Promise<AIAnalysisResult[]> {
    // Fallback to non-streaming for compatibility
    const client = AIClientFactory.createClient({
      provider: 'anthropic',
      anthropicApiKey: this.apiKey,
      selectedModel: this.model,
      maxTokens: this.maxTokens
    } as AISettings);
    
    return client.analyzeCorrections(request);
  }

  async isAvailable(): Promise<boolean> {
    return !!this.apiKey && this.model !== '';
  }

  getModelName(): string {
    return this.model;
  }

  /**
   * Send a message to Anthropic and get a response
   */
  async sendMessage(prompt: string): Promise<string> {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'x-api-key': this.apiKey,
        'anthropic-version': '2023-06-01',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: this.model,
        messages: [{ role: 'user', content: prompt }],
        max_tokens: this.maxTokens,
        temperature: 0.1
      })
    });

    if (!response.ok) {
      throw new Error(`Anthropic API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.content?.[0]?.text || '';
  }

  /**
   * Anthropic streaming implementation using server-sent events
   */
  async analyzeCorrectionsStreaming(
    request: AIAnalysisRequest,
    onProgress?: (chunk: string) => void,
    onComplete?: (results: AIAnalysisResult[]) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'x-api-key': this.apiKey,
          'Content-Type': 'application/json',
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: this.model,
          max_tokens: this.maxTokens,
          stream: true, // Enable streaming
          messages: [
            {
              role: 'user',
              content: this.buildAnthropicPrompt(request)
            }
          ]
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Anthropic API error: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      // Process streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete lines (Anthropic uses SSE format)
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          for (const line of lines) {
            if (line.trim() === '') continue;
            
            // Handle Anthropic SSE format
            if (line.startsWith('data: ')) {
              try {
                const jsonData = line.substring(6);
                const parsed = JSON.parse(jsonData);
                
                // Handle different event types
                if (parsed.type === 'content_block_delta') {
                  const content = parsed.delta?.text;
                  if (content) {
                    fullContent += content;
                    onProgress?.(content);
                  }
                } else if (parsed.type === 'message_stop') {
                  // Stream complete
                  break;
                }
              } catch (parseError) {
                console.warn('[Anthropic Streaming] Failed to parse line:', line);
              }
            } else if (line.startsWith('event: ')) {
              // Handle event type lines (Anthropic SSE format)
              continue;
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // Parse final response and complete
      const results = this.parseResponse(fullContent);
      onComplete?.(results);
      
    } catch (error) {
      const streamingError = error instanceof Error ? error : new Error('Streaming failed');
      onError?.(streamingError);
      throw streamingError;
    }
  }

  /**
   * Create client for fallback settings
   */
  protected async createClientForSettings(settings: AISettings): Promise<AIClient> {
    return AIClientFactory.createClient(settings);
  }

  /**
   * Build Anthropic prompt (combines system and user messages)
   */
  private buildAnthropicPrompt(request: AIAnalysisRequest): string {
    const systemPrompt = `You are a Korean grammar expert. Analyze the given text and grammar errors to suggest the most appropriate corrections.

Follow these rules:
1. Choose the most contextually appropriate correction
2. If the original text is better than all suggestions, set keepOriginal: true
3. If the provided suggestions are incorrect or inappropriate, you can suggest better alternatives
4. If suggestion words themselves have typos or don't fit the context, suggest the correct forms
5. Consider exception processing for proper nouns, URLs, emails, technical terms
6. Provide a brief reason for each choice

Important:
- Don't limit yourself to provided options, suggest better corrections when needed
- If grammar checker suggestions are wrong, propose correct expressions
- If original is correct, set keepOriginal: true

Response format in JSON:
[
  {
    "correctionIndex": 0,
    "selectedValue": "selected value or better alternative",
    "isExceptionProcessed": false,
    "keepOriginal": false,
    "reasoning": "contextually this word is most appropriate / corrected wrong suggestion to proper form"
  }
]`;
    
    return `${systemPrompt}\n\n${this.formatUserPrompt(request)}`;
  }

  /**
   * Format user prompt for Anthropic
   */
  private formatUserPrompt(request: AIAnalysisRequest): string {
    const correctionCount = request.corrections.length;
    let prompt = `Context: "${request.contextText}"\n\nErrors to correct (TOTAL: ${correctionCount}):\n`;

    request.corrections.forEach((correction: any, index: number) => {
      const original = correction.original || '';
      const corrected = Array.isArray(correction.corrected)
        ? correction.corrected.join(', ')
        : (correction.corrected || '');

      prompt += `${index + 1}. "${original}" → Options: [${corrected}]\n`;
      if (correction.help) {
        prompt += `   Help: ${correction.help}\n`;
      }
    });

    prompt += `\nCRITICAL: You MUST provide exactly ${correctionCount} entries in your response. Each error above MUST have a corresponding correction entry. DO NOT skip any errors.`;

    return prompt;
  }

  /**
   * Parse Anthropic response to AIAnalysisResult[]
   */
  private parseResponse(content: string): AIAnalysisResult[] {
    try {
      // Try direct JSON parsing first
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // Direct parsing failed, try other methods
      }
      
      // Extract JSON from code blocks (```json ... ```)
      let jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch && jsonMatch[1]) {
        return JSON.parse(jsonMatch[1]);
      }
      
      // Find JSON array starting with bracket
      jsonMatch = content.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      
      throw new Error('JSON format not found in response');
    } catch (error) {
      console.error('[Anthropic Streaming] Response parsing failed:', error);
      console.error('[Anthropic Streaming] Original response:', content);
      throw new Error(`Failed to parse AI response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}